import {useMutation} from "@apollo/client";
import pluralize from "pluralize";

import {ChecksModalButton} from "@/components/ChecksModalButton/ChecksModalButton.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {GQL_DELETE_PRIVATE_LOCATION, GQL_PRIVATE_LOCATIONS_LIST_QUERY} from "@/lib/queries/monitoring.ts";
import {GraphQLResponse, ResponseStatus} from "@/lib/types/common.ts";
import {PrivateLocationWithGlobalStatus} from "@/lib/types/monitoring.ts";

import styles from "./PrivateLocationActionDelete.module.scss";

export interface PrivateLocationActionDeleteProps {
  show: boolean;
  setShow: (show: boolean) => void;
  location: PrivateLocationWithGlobalStatus;
}

export const PrivateLocationActionDeleteDialog = ({location, show, setShow}: PrivateLocationActionDeleteProps) => {
  const [deletePrivateLocation, {data, reset, loading}] = useMutation<GraphQLResponse<null>>(
    GQL_DELETE_PRIVATE_LOCATION,
    {
      refetchQueries: [GQL_PRIVATE_LOCATIONS_LIST_QUERY],
    }
  );

  const handleSubmit = async () => {
    const response = await deletePrivateLocation({
      variables: {
        server_id: location.id,
      },
    });
    if (response.data?.response.status === ResponseStatus.SUCCESS) {
      setShow(false);
    }
  };

  const serverErrors = data?.response?.status === ResponseStatus.ERROR ? data?.response?.message.__all__ : [];

  return (
    <>
      <ActionDialog
        title={serverErrors?.length > 0 ? "Unable to Delete Private Location" : "Delete Private Location"}
        show={show}
        loading={loading}
        onConfirm={handleSubmit}
        onCancel={() => {
          setShow(false);
          reset();
        }}
        confirmBtnText="Delete"
        hasConfirmButton={serverErrors?.length === 0}
        hasCancelButton={true}
      >
        {serverErrors?.length > 0 && serverErrors[0]}
        {serverErrors?.length === 0 && (
          <>
            Are you sure you want to permanently delete the Private Location <strong>{location.name}</strong>?
            {location.num_services > 0 && (
              <>
                {" "}
                This will remove this location from all linked checks (
                <ChecksModalButton
                  id={location.id}
                  numberOfChecks={location.num_services}
                  model_type={"MonitoringServer"}
                  title={"Private Location: " + location.name}
                  className={styles.checkCount}
                >
                  {location.num_services} {pluralize("check", location.num_services)}
                </ChecksModalButton>
                ).
              </>
            )}
          </>
        )}
      </ActionDialog>
    </>
  );
};
